import React from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/authStore';
import { apiClient } from '@/lib/apiClient';
import { isTokenValid, MARKETING_URL, LOGIN_PATH } from '@/lib/auth';
import type { ApiResponse, User } from '@/types/api';

/**
 * Guards protected routes. Checks, in order:
 *  1. No token / client-side-expired token -> immediate logout + redirect (no network call).
 *  2. Otherwise verify the token SERVER-SIDE via GET /api/auth/me:
 *       - success -> refresh the persisted user, render the page.
 *       - failure (revoked token, deleted user, rotated secret, expiry) -> log out.
 *
 * The global 401 interceptor also hard-redirects on any 401, so an invalid
 * /auth/me response logs the user out even if this component is mid-render.
 */
export function AuthGuard({ children }: { children: React.ReactNode }) {
  const { token, setAuth, clearAuth } = useAuthStore();
  const hasUsableToken = isTokenValid(token);

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () =>
      apiClient.get<ApiResponse<User>>('/api/auth/me').then((res) => res.data.data as User),
    enabled: hasUsableToken,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // Refresh the persisted user whenever /auth/me returns fresh data.
  React.useEffect(() => {
    if (meQuery.data && token) setAuth(token, meQuery.data);
  }, [meQuery.data, token, setAuth]);

  // Log out on: no usable token, or a rejected /auth/me.
  const mustLogout = !hasUsableToken || meQuery.isError;
  React.useEffect(() => {
    if (mustLogout) {
      clearAuth();
      if (MARKETING_URL) window.location.replace(MARKETING_URL);
    }
  }, [mustLogout, clearAuth]);

  if (mustLogout) {
    // External marketing zone -> full-page redirect (handled by the effect);
    // otherwise bounce to the in-app login route.
    return MARKETING_URL ? null : <Navigate to={LOGIN_PATH} replace />;
  }

  // Token present but still confirming with the server.
  if (meQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3 text-slate-500">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
          <span className="text-sm">Verifying your session…</span>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
